import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import styled from "styled-components/macro";
import useSWR from "swr";
import {
  DestructiveButtonMediumV2,
  LinkButtonLargeV2,
  PrimaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { SectionTitle } from "../../../../../components/Form/Form";
import {
  ArchiveIcon,
  CaretLeft,
  EditIcon,
  SystemDefaultIcon,
} from "../../../../../components/Icons/Icons";
import { SlideOut } from "../../../../../components/SlideOut/SlideOut";
import {
  H6,
  SoftHeader2,
} from "../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../endpoints";
import type {
  AttributeSchema,
  PortfolioControlsSchema,
  ShortListSchema,
} from "../../../../../types/types.PIM";
import {
  removeUnderscore,
  toTitleCase,
  useHasMultipleLanguages,
  useStoreState,
} from "../../../../../util/util";
import { EditAttribute } from "./EditAttribute";
import type { AxiosError } from "axios";
import Axios from "axios";
import { useRoutePath } from "../../../../../util/Routing";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import { AttributesNav } from "../components/AttributesNav";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import ReactTooltip from "react-tooltip";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { DeletableChips } from "../../../../../components/DeletableChips/DeletableChips";
import noop from "lodash/noop";
import { useAuthContext } from "../../../../../components/Auth";
import { Flex } from "../../../../../layout/FormLayout";
import { ObjectReferenceDetail } from "../../../../../components/ObjectReference/ObjectReferenceDetail";
import { SystemIconWrapper } from "../components/PIM.components.util";
import { AttributeTranslations } from "./AttributeTranslations";
import {
  getAddressTableColumns,
  getAdvancedAddressFields,
  getStandardAddressFields,
} from "./AddressAttributes/address.utils";
import { Table } from "../../../../../components/Table/Table";
import { TemplatesNavWrapper } from "../SellerAdminTemplates/TemplatesNav";
import { TextSmRegular } from "../../../../../components/Typography/TypographyV2";
import { spacingTokens } from "../../../../../designTokens";

const AttributeDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
`;

const AttributeItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const AttributeDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  margin: 0;
  padding: 0;
  max-width: 300px;
  hyphens: auto;
`;

const DeleteTextContainer = styled.div`
  color: ${({ theme }) => theme.errorColor};
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")};
  align-items: center;
`;

const EditButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  align-items: center;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
`;

const ButtonsContainer = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_5xl")};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  width: fit-content;
`;

const hasList = (inputType: string) =>
  inputType === "single_select" || inputType === "multi_select";

function AttributeDisplayConfigurationDetail({
  attribute,
}: {
  attribute: AttributeSchema;
}) {
  const { t } = useTranslation();
  return (
    <AttributeItem>
      <TextSmRegular color="secondary">
        {t("Display Configuration")}
      </TextSmRegular>
      <H6>
        {"display_configuration" in attribute &&
        attribute.display_configuration === "product_card"
          ? attribute.input_type === "product_reference"
            ? t("Product Card")
            : t("Grid")
          : attribute.input_type === "product_reference"
          ? t("Link")
          : t("List")}
      </H6>
    </AttributeItem>
  );
}

export const AttributeDetailPage = () => {
  const [showEditSlideOut, setEditSlideOut] = useState(false);
  const [isPortfolioControl, setIsPortfolioControl] = useState<boolean>(false);
  const [showArchiveDialogBox, setArchiveDialogBox] = useState(false);
  const { id: attribute_id } = useParams<{ id: string }>();
  const { tenant_id } = useStoreState();
  const { adminPath } = useRoutePath();
  const {
    data: attribute,
    mutate,
    error: attributeError,
  } = useSWR<AttributeSchema>([
    endpoints.v2_tenants_id_pim_attributes_id(tenant_id, attribute_id),
    useMemo(() => ({ params: { include_translations: true } }), []),
  ]);

  const { data: list } = useSWR<ShortListSchema>(
    !!attribute && hasList(attribute.input_type)
      ? endpoints.v2_tenants_id_or_slug_pim_lists_id(
          tenant_id,
          attribute.list_id
        )
      : null,
    { revalidateOnFocus: false }
  );

  const { t } = useTranslation();
  const standardFields = getStandardAddressFields(t);
  const advancedFields = getAdvancedAddressFields(t);
  const addressTableColumns = getAddressTableColumns(t);
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();
  const history = useHistory();
  const location = useLocation();
  const [query] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const closeEditSlideOut = () => setEditSlideOut(false);
  const closeArchiveDialogBox = () => setArchiveDialogBox(false);
  const { hasPermission } = useAuthContext();
  const hasMultipleLanguages = useHasMultipleLanguages();

  useSWR<{ data: PortfolioControlsSchema[] }>(
    `v2/storefronts/${storefront_id}/portfolio-controls`,
    {
      onSuccess: ({ data: controlsData }) => {
        setIsPortfolioControl(
          controlsData
            .map((control) => control.attribute.new_object_type)
            .indexOf(attribute?.new_object_type || "") > -1
        );
      },
      revalidateOnFocus: true,
    }
  );

  const archiveAttribute = async () => {
    try {
      await Axios.delete(
        endpoints.v2_tenants_id_pim_attributes_id(tenant_id!, attribute_id)
      );
      notifySuccess(t("Attribute archived successfully"));
      history.push(`${adminPath}/pim/attributes`);
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("Could not archive attribute. Something went wrong."),
        {
          error,
        }
      );
    }
  };

  const isLoadingAttributeDetail = !attribute && !attributeError;

  if (attributeError) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the attribute")}
      />
    );
  }

  if (isLoadingAttributeDetail) {
    return <DelayedSpinner />;
  }

  const params = new URLSearchParams();

  if (query.q) {
    params.append("q", query.q);
  }

  params.append("offset", String(query?.offset ?? 0));
  params.append("perPage", String(query?.perPage ?? 10));

  const goBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault(); // Prevent the default link behavior
    history.push({
      pathname: location.search.includes("from=dashboard")
        ? `${adminPath}/dashboard`
        : `${adminPath}/pim/attributes`,
      search: params.toString(),
      state: location.state,
    });
  };

  return (
    <PageWrapper>
      <TemplatesNavWrapper>
        <AttributesNav tabName="Attributes" params={params} />
      </TemplatesNavWrapper>
      {attribute && (
        <AttributeDetailContainer>
          <div style={{ marginLeft: "-2px" }}>
            <Link
              to={
                location.search.includes("from=dashboard")
                  ? `${adminPath}/dashboard`
                  : `${adminPath}/pim/attributes?${params}`
              }
              onClick={goBack}
            >
              <LinkButtonLargeV2>
                <CaretLeft fill="currentColor" />
                {t("Attributes")}
              </LinkButtonLargeV2>
            </Link>
          </div>
          <Flex
            style={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <SectionTitle style={{ marginTop: spacingTokens.spacing_2xl }}>
              {t("Attribute Definition")}
            </SectionTitle>
            {attribute.is_system && (
              <Flex
                style={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "nowrap",
                }}
              >
                <SystemIconWrapper
                  data-for={`system${attribute.id}`}
                  data-tip={t("System Default")}
                >
                  <SystemDefaultIcon width={22} height={22} />
                </SystemIconWrapper>
                <SoftHeader2>{t("System default")}</SoftHeader2>
              </Flex>
            )}
          </Flex>
          <AttributeItem>
            <TextSmRegular color="secondary">
              {t("Attribute Name")}
            </TextSmRegular>
            <H6>{attribute.name}</H6>
          </AttributeItem>
          <AttributeItem>
            <TextSmRegular color="secondary">{t("Display Name")}</TextSmRegular>
            <H6>
              {attribute.display_name ? t([attribute.display_name]) : "--"}
            </H6>
          </AttributeItem>
          <AttributeItem>
            <TextSmRegular color="secondary">
              {t("Short description")}
            </TextSmRegular>
            <AttributeDescription>
              {attribute.description ? t([attribute.description]) : "--"}
            </AttributeDescription>
          </AttributeItem>
          <SectionTitle>{t("Attribute Type")}</SectionTitle>
          <AttributeItem>
            <TextSmRegular color="secondary">
              {t("Attribute Type")}
            </TextSmRegular>
            <H6>{toTitleCase(removeUnderscore(attribute.input_type))}</H6>
          </AttributeItem>
          {attribute.input_type === "product_reference" && (
            <>
              <AttributeDisplayConfigurationDetail attribute={attribute} />
              <AttributeItem>
                <TextSmRegular color="secondary">
                  {t("Selection Type")}
                </TextSmRegular>
                <H6>
                  {toTitleCase(
                    removeUnderscore(
                      attribute.is_multiple_choice
                        ? "Multiple products"
                        : "Single product"
                    )
                  )}
                </H6>
              </AttributeItem>
            </>
          )}
          {attribute.input_type === "user" && (
            <AttributeItem>
              <TextSmRegular color="secondary">
                {t("Selection Type")}
              </TextSmRegular>
              <H6>
                {toTitleCase(
                  removeUnderscore(
                    attribute.is_multiple_choice
                      ? "Multiple users"
                      : "Single user"
                  )
                )}
              </H6>
            </AttributeItem>
          )}
          {attribute.input_type === "address" && (
            <>
              <AttributeItem>
                <TextSmRegular color="secondary">
                  {t("Attribute Mode")}
                </TextSmRegular>
                <H6>
                  {toTitleCase(
                    removeUnderscore(
                      attribute.address_mode === "advanced"
                        ? "Advanced"
                        : "Standard"
                    )
                  )}
                </H6>
              </AttributeItem>

              <AttributeItem style={{ marginTop: "10px" }}>
                <Table
                  columns={addressTableColumns}
                  data={
                    attribute.address_mode === "advanced"
                      ? advancedFields
                      : standardFields
                  }
                  isLoading={false}
                  error={undefined}
                  lastChildleftAlign={true}
                />
              </AttributeItem>
            </>
          )}
          {attribute.input_type === "object_reference" && (
            <ObjectReferenceDetail
              attribute={
                attribute as Extract<
                  AttributeSchema,
                  { input_type: "object_reference" }
                >
              }
            />
          )}
          {hasList(attribute.input_type) && (
            <>
              <AttributeItem>
                <TextSmRegular color="secondary">
                  {t("Chosen List")}
                </TextSmRegular>
                <H6>{list?.parent ? list?.parent?.name : list?.name ?? ""}</H6>
                {!!list?.parent && (
                  <TextSmRegular
                    color="secondary"
                    style={{ marginTop: spacingTokens.spacing_xs }}
                  >
                    {t("Selected values from list")}
                  </TextSmRegular>
                )}
              </AttributeItem>
              {!!attribute.choices &&
                attribute.choices.length > 0 &&
                !!list?.parent && (
                  <DeletableChips
                    chips={
                      Array.from(attribute.choices)?.map?.((choice) => ({
                        name: choice,
                        active: false,
                      })) ?? []
                    }
                    noneSelectedName="choices"
                    handleClick={noop}
                  />
                )}

              {(attribute.input_type === "single_select" ||
                attribute.input_type === "multi_select") && (
                <>
                  <AttributeItem>
                    <TextSmRegular color="secondary">
                      {t("Show Icons")}
                    </TextSmRegular>
                    <H6>{attribute.icons_enabled ? t("Yes") : t("No")}</H6>
                  </AttributeItem>
                  {attribute.icons_enabled && (
                    <AttributeItem>
                      <TextSmRegular color="secondary">
                        {t("Icon Size")}
                      </TextSmRegular>
                      <H6>{toTitleCase(attribute.size || "--")}</H6>
                    </AttributeItem>
                  )}
                </>
              )}
            </>
          )}
          <ButtonsContainer>
            {hasPermission("delete_attributes") && (
              <DestructiveButtonMediumV2
                disabled={attribute.is_system || isPortfolioControl}
                onClick={() => setArchiveDialogBox(true)}
              >
                <DeleteTextContainer
                  data-for="attribute-archive-delete-buttons"
                  data-tip={
                    attribute.is_system
                      ? t("You cannot archive a system default attribute.")
                      : isPortfolioControl && !attribute.is_system
                      ? t("You cannot archive a portfolio control attribute.")
                      : ""
                  }
                >
                  <ArchiveIcon />
                  {t("Archive")}
                </DeleteTextContainer>
              </DestructiveButtonMediumV2>
            )}
            {hasPermission("modify_attributes") && (
              <PrimaryButtonMediumV2
                style={{ padding: 0 }}
                onClick={() => setEditSlideOut(true)}
              >
                <EditButtonContainer>
                  <EditIcon />
                  {t("Edit")}
                </EditButtonContainer>
              </PrimaryButtonMediumV2>
            )}
            <ReactTooltip id="attribute-archive-delete-buttons" />
          </ButtonsContainer>
          {hasMultipleLanguages ? (
            <div style={{ marginTop: spacingTokens.spacing_5xl }}>
              <AttributeTranslations attribute={attribute} mutate={mutate} />
            </div>
          ) : (
            <></>
          )}
          <SlideOut
            title={t("Edit Attribute")}
            closeFlyout={closeEditSlideOut}
            show={showEditSlideOut}
          >
            <EditAttribute
              attribute={attribute}
              onComplete={closeEditSlideOut}
              refreshAttribute={mutate}
              list={list}
              onCancel={closeEditSlideOut}
            />
          </SlideOut>
          <ConfirmDialog
            show={showArchiveDialogBox}
            confirmBtnLabel={t("Archive")}
            confirmBtnType="destructive"
            confirmMessage={t(
              "Are you sure you want to archive this attribute?"
            )}
            closeDialog={closeArchiveDialogBox}
            handleConfirm={archiveAttribute}
          />
        </AttributeDetailContainer>
      )}
    </PageWrapper>
  );
};
